import type { Theme, SxProps } from '@mui/material/styles';

import { useColorScheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Iconify } from 'src/components/iconify';
import { Map, MapPopup, MapMarker, MapControls } from 'src/components/map';

// ----------------------------------------------------------------------

type ContactMapProps = {
  sx?: SxProps<Theme>;
  contacts: {
    latlng: number[];
    address: string;
    phoneNumber: string;
  }[];
};

export function ContactMap({ contacts, sx }: ContactMapProps) {
  const { mode } = useColorScheme();

  const [popupInfo, setPopupInfo] = useState<ContactMapProps['contacts'][0] | null>(null);

  return (
    <Map
      initialViewState={{ latitude: 41, longitude: 60, zoom: 1 }}
      mapStyle={`mapbox://styles/mapbox/${mode === 'light' ? 'light' : 'dark'}-v10`}
      sx={[
        () => ({
          borderRadius: 0.5,
          height: { xs: 320, md: 560 },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}>
      <MapControls hideGeolocate />

      {contacts.map((country, index) => (
        <MapMarker
          key={`marker-${index}`}
          latitude={country.latlng[0]}
          longitude={country.latlng[1]}
          onClick={(event) => {
            event.originalEvent.stopPropagation();
            setPopupInfo(country);
          }}
        />
      ))}

      {popupInfo && (
        <MapPopup
          longitude={popupInfo.latlng[1]}
          latitude={popupInfo.latlng[0]}
          onClose={() => setPopupInfo(null)}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Address
          </Typography>

          <Typography component="div" variant="caption">
            {popupInfo.address}
          </Typography>

          <Typography
            component="div"
            variant="caption"
            sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
            <Iconify icon="solar:phone-bold" width={14} sx={{ mr: 0.5 }} />
            {popupInfo.phoneNumber}
          </Typography>
        </MapPopup>
      )}
    </Map>
  );
}
